import { LeelooUser } from '@src/types/users.ts';
import { TOption } from '@src/types/dropdown.ts';

import { ProductTradingAccountTypes } from './productsAddons';

export type TReportNewUserByMonthParams = {
  start_date: string | null;
  end_date: string | null;
};

export type TReportNewUserByMonth = {
  date: string;
  count: number;
};

export type TReportNewAvalAccountByMonthParams = {
  month: string; // Format: 'YYYY-MM'
};

export type TReportNewAvalAccountByMonthData = {
  package: string;
  new_accounts: number;
  renewals: number;
};

export type TReportPANewRenewalResetByMonthParams = {
  month: string; // Format: 'YYYY-MM'
};

export type TReportPANewRenewalResetByMonthData = {
  package: string;
  new_accounts: number;
  renewals: number;
  resets: number;
};

export type TReportPANewRenewalResetByMonthRes = {
  mode: string;
  title: string;
  labels: string[];
  new_accounts: number[];
  renewals: number[];
  resets: number[];
  new_total: number;
  renewal_total: number;
  reset_total: number;
  generated_at: string;
};

export type TPaNewAccountByDateParams = {
  from_date: string;
  to_date: string;
  page?: number;
  page_size?: number;
};

export type TPaNewAccountByDateRow = {
  name: string;
  email: string;
  username: string;
  account_name: string;
  created_on: string;
};

export type TPaNewAccountByDateRes = {
  data: TPaNewAccountByDateRow[];
  total_accounts: number;
  total_users: number;
  page: number;
  page_size: number;
  total_pages: number;
  generated_at: string;
};

export type TPaResetParams = {
  from_date: string;
  to_date: string;
  page?: number;
  page_size?: number;
};

export type TPaResetPackageOption = TOption;

export type TPaResetRow = {
  user_id: number;
  name: string;
  reset_on: string;
  account_id: string;
  reset_from: string;
  package: string;
  valid_from: string;
  valid_to: string;
};

export type TPaResetRes = {
  data: TPaResetRow[];
  package_options: TPaResetPackageOption[];
  total_accounts: number;
  page: number;
  page_size: number;
  total_pages: number;
  generated_at: string;
};

export type TStaffFreebieParams = {
  from_date?: string;
  to_date?: string;
};

export type TStaffFreebieMonthOption = {
  label: string;
  value: string;
};

export type TStaffFreebieChartData = {
  labels: string[];
  resets: number[];
  new_accounts: number[];
  refunds: number[];
};

export type TStaffFreebieUserDetail = {
  leeloo_user_id: number;
  display_name: string;
  resets: number;
  reset_reasons: string[];
  new_accounts: number;
  new_account_reasons: string[];
  refunds: number;
  refund_reasons: string[];
};

export type TStaffFreebieStaffDetail = {
  staff_user_id: number;
  staff_name: string;
  resets: number;
  new_accounts: number;
  refunds: number;
  users: TStaffFreebieUserDetail[];
};

export type TStaffFreebieRes = {
  month_options_from: TStaffFreebieMonthOption[];
  month_options_to: TStaffFreebieMonthOption[];
  chart: TStaffFreebieChartData;
  staffs: TStaffFreebieStaffDetail[];
};

export type TReportEvalAccountByMonthParams = {
  month: string; // Use 'all' for all-time totals or 'YYYY-MM' for a specific month
};

export type TReportEvalAccountByMonthRes = {
  mode: string;
  title: string;
  labels: string[];
  new_accounts: number[];
  renewals: number[];
  new_total: number;
  renewal_total: number;
  generated_at: string;
};

export type TAddedByUser = {
  first_name: string;
  last_name: string;
  email: string;
};

export type TReportBlacklistUser = {
  added_by_user: TAddedByUser;
  added_by_user_id: string;
  date_added: string;
  id: number;
  leeloo_user: LeelooUser;
};

export type TReportBlacklistUsersParams = {
  start_date?: string | null;
  end_date?: string | null;
  page?: number;
  size?: number;
};

export type TReportBlacklistUsersRes = {
  current_page: number;
  data: TReportBlacklistUser[];
  has_next: boolean;
  total: number;
  total_pages: number;
};

export type TExpressPAAccountRefundData = {
  user_id: number;
  username: string;
  express_account_name: string;
  pa_account_name: string;
  invoice_id: number;
  amount: string;
  refunded_amount: string;
  refunded_on: string;
};

export type TExpressPAAccountRefundParams = {
  created_on: string; // Format: YYYY-MM-DD
};

export type TExpressPAAccountRefundRes = {
  data: TExpressPAAccountRefundData[];
  total: number;
  generated_at: string;
};

export type TExpiryDateUser = {
  id: number;
  account_valid_to: string;
  active: number;
  closed: number;
  expire_date: string;
  user: TAddedByUser;
  username: string;
};

export type TReportExpiryDateUserRes = {
  current_page: number;
  data: TExpiryDateUser[];
  has_next: boolean;
  total: number;
  total_pages: number;
};

export type TReportActiveUsersWithNoAccounts = {
  active: number;
  closed: number;
  cbot: boolean;
  cme: boolean;
  comex: boolean;
  eurex: boolean;
  expire_date: string;
  id: number;
  market_depth: boolean;
  nymex: boolean;
  user: TAddedByUser;
  username: string;
};

export type TReportUsersWithExtraSession = {
  active: number;
  closed: number;
  cbot: boolean;
  cme: boolean;
  comex: boolean;
  eurex: boolean;
  expire_date: string;
  id: number;
  max_session_data: boolean;
  nymex: boolean;
  user: TAddedByUser;
  username: string;
};

export type TReportUsersWithTopOfBook = {
  active: number;
  closed: number;
  cbot: boolean;
  cbot_depth: boolean;
  cme: boolean;
  cme_depth: boolean;
  comex: boolean;
  comex_depth: boolean;
  eurex: boolean;
  eurex_depth: boolean;
  expire_date: string;
  id: number;
  max_session_data: boolean;
  nymex: boolean;
  nymex_depth: boolean;
  q: boolean;
  q_depth: boolean;
  user: TAddedByUser;
  username: string;
};
export type TReportChurnByDate = {
  total_users: number;
  active: number;
  expired: number;
  active_percent: string;
  expired_percent: string;
};

export type TReportCommonPayloadWithGroupingAndProducts = {
  start_date: string | null;
  end_date: string | null;
  grouping: string;
  products?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportCommonPayloadWithGrouping = Omit<
  TReportCommonPayloadWithGroupingAndProducts,
  'products'
>;

export type TReportActiveUsersByPeriodData = {
  period: string;
  active_users: number;
  active_users_previous: number | null;
  active_users_previous_year: number | null;
};

export type TReportActiveUsersByPeriodRes = {
  summary: {
    active_users: number;
    active_users_previous: number;
    active_users_previous_year: number;
  };
  data: TReportActiveUsersByPeriodData[];
};

export type TReportOldAffiliateClicksData = {
  date: string;
  unique_click: number;
  all_click: number;
  previous_period_unique_click: number | null;
  previous_period_all_click: number | null;
  previous_year_unique_click: number | null;
  previous_year_all_click: number | null;
  all_click_previous?: number | null;
  all_click_previous_year?: number | null;
  unique_click_previous?: number | null;
  unique_click_previous_year?: number | null;
};

export type TReportOldAffiliateClicksPeriod = {
  label: string;
  start_date: string;
  end_date: string;
  total_unique_click: number;
  total_all_click: number;
};

export type TReportOldAffiliateClicksRes = {
  data: TReportOldAffiliateClicksData[];
  summary: {
    total_unique_click: number;
    total_all_click: number;
  };
  previous_period: TReportOldAffiliateClicksPeriod | null;
  previous_year: TReportOldAffiliateClicksPeriod | null;
  comparison_to_previous: {
    unique_click_change: number;
    all_click_change: number;
  } | null;
  comparison_to_previous_year: {
    unique_click_change: number;
    all_click_change: number;
  } | null;
  grouping: string;
  period: TReportOldAffiliateClicksPeriod;
};

export type TReportNewAffiliateClicksData = {
  period: string;
  all_clicks: number;
  unique_clicks: number;
  all_clicks_previous: number | null;
  unique_clicks_previous: number | null;
  all_clicks_previous_year: number | null;
  unique_clicks_previous_year: number | null;
};

export type TReportNewAffiliateClicksRes = {
  summary: {
    all_clicks: number;
    unique_clicks: number;
    all_clicks_previous: number;
    unique_clicks_previous: number;
    all_clicks_previous_year: number;
    unique_clicks_previous_year: number;
  };
  data: TReportNewAffiliateClicksData[];
};

export type TReportOldAffiliateByUsersData = {
  username: string;
  commission: number;
};

export type TReportOldAffiliateByUsersRes = {
  data: TReportOldAffiliateByUsersData[];
};

export type TReportNewAffiliateByUsersData = {
  user_name: string;
  commissions: number;
};

export type TReportNewAffiliateByUsersRes = {
  data: TReportNewAffiliateByUsersData[];
};

export type TReportActiveUsersByProductCategoriesPayload = {
  account_type?: string[];
  add_ons?: boolean;
  monthly_subscription?: boolean;
};

export type TReportActiveUsersByProductCategoriesData = {
  category: string;
  active_users: number;
};

export type TReportActiveUsersByProductCategoriesRes = {
  data: TReportActiveUsersByProductCategoriesData[];
  summary: {
    active_users: number;
  };
};

export type TReportActiveUsersByProductPayload = {
  products?: number[];
};

export type TReportActiveUsersByProductData = {
  product: string;
  active_users: number;
};

export type TReportActiveUsersByProductRes = {
  data: TReportActiveUsersByProductData[];
  summary: {
    active_users: number;
  };
};

export type TReportAmountOfRebillsParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
};

export type TReportAmountOfRebillsData = {
  date: string;
  amount_of_rebills: number;
};

export type TReportAmountOfRebillsRes = {
  data: TReportAmountOfRebillsData[];
};

export type TReportNumberOfRebillsParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportNumberOfRebillsData = {
  date: string;
  rebills: number;
  rebills_previous?: number;
  rebills_previous_year?: number;
};

export type TReportNumberOfRebillsRes = {
  data: TReportNumberOfRebillsData[];
};

export type TReportOldAffiliateCommissionsParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportOldAffiliateCommissionsData = {
  report_date: string;
  commission: number;
  previous_period_commission?: number;
  previous_year_commission?: number;
};

export type TReportOldAffiliateCommissionsRes = {
  data: TReportOldAffiliateCommissionsData[];
  total_commission: number;
  previous_period_total?: number;
  previous_year_total?: number;
};

export type TReportNewAffiliateCommissionsParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportNewAffiliateCommissionsData = {
  date: string;
  commissions: number;
  commissions_previous?: number;
  commissions_previous_year?: number;
};

export type TReportNewAffiliateCommissionsRes = {
  data: TReportNewAffiliateCommissionsData[];
  total: number;
};

// Affiliate Commissions By Product Types
export type TReportOldAffiliateCommissionsByProductParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  products?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportOldAffiliateCommissionsByProductData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TReportOldAffiliateCommissionsByProductRes = {
  data: TReportOldAffiliateCommissionsByProductData[];
  total_by_product: Record<string, number>;
  available_products: string[];
};

export type TReportNewAffiliateCommissionsByProductParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  products?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportNewAffiliateCommissionsByProductData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TReportNewAffiliateCommissionsByProductRes = {
  data: TReportNewAffiliateCommissionsByProductData[];
  total_by_product: Record<string, number>;
  available_products: string[];
};

// Affiliate Sale Number Types
export type TReportOldAffiliateSaleNumberParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportOldAffiliateSaleNumberData = {
  date: string;
  sales: number;
  sales_previous?: number;
  sales_previous_year?: number;
  isTotal?: boolean;
};

export type TReportOldAffiliateSaleNumberRes =
  TReportOldAffiliateSaleNumberData[];

export type TReportNewAffiliateSaleNumberParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportNewAffiliateSaleNumberData = {
  date: string;
  commissions: number;
  commissions_previous?: number;
  commissions_previous_year?: number;
};

export type TReportNewAffiliateSaleNumberRes = {
  data: TReportNewAffiliateSaleNumberData[];
  total: number;
};

export type TReportPaymentDistributionByCustomerCountryParams = {
  countries: string[];
};

export type TReportPaymentDistributionByCustomerCountryData = {
  country: string;
  amount: number;
};

export type TReportPaymentDistributionByCustomerCountryRes = {
  data: TReportPaymentDistributionByCustomerCountryData[];
};

export type TReportRefundsDistributionByCustomerCountryParams = {
  countries?: string[];
};

export type TReportRefundsDistributionByCustomerCountryData = {
  country: string;
  amount: number;
};

export type TReportRefundsDistributionByCustomerCountryRes = {
  data: TReportRefundsDistributionByCustomerCountryData[];
};

export type TReportPaymentsByCustomerCityOrCountryPayload = {
  grouping?: string;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
  by_city?: boolean;
  cities?: string[];
  by_country?: boolean;
  countries?: string[];
};

export type TReportPaymentsByCustomerCityOrCountryData = {
  date: string;
  amount: Record<string, number>;
  amount_previous?: Record<string, number>;
  amount_previous_year?: Record<string, number>;
};

export type TReportPaymentsByCustomerCityOrCountryRes = {
  data: TReportPaymentsByCustomerCityOrCountryData[];
};

export type TReportPaymentsByCustomerCountryData = {
  date: string;
  countries: Record<string, number>;
  countries_previous?: Record<string, number>;
  countries_previous_year?: Record<string, number>;
};

export type TReportPaymentsByCustomerCountryRes = {
  data: TReportPaymentsByCustomerCountryData[];
};

export type TReportPaymentsByCustomerCityOption = {
  value: string;
  label: string;
};

export type TReportPaymentsByCustomerCityOptionsRes = {
  data: TReportPaymentsByCustomerCityOption[];
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
};

export type TReportRefundsByCustomerCountryParams = {
  grouping: string;
  start_date: string | null;
  end_date: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
  countries?: string[];
};

export type TReportRefundsByCustomerCountryData = {
  date: string;
  countries: Record<string, number>;
  countries_previous?: Record<string, number>;
  countries_previous_year?: Record<string, number>;
};

export type TReportRefundsByCustomerCountryRes = {
  data: TReportRefundsByCustomerCountryData[];
};

export type TReportAverageAmountByProductsBreakdownParams = {
  start_date?: string | null;
  end_date?: string | null;
  product_ids?: number[];
};

export type TReportAverageAmountByProductsBreakdownData = {
  product_id: number;
  product_name?: string | null;
  average_amount: number;
  average_count_of_purchases: number;
};

export type TReportAverageAmountByProductsBreakdownRes =
  TReportAverageAmountByProductsBreakdownData[];

export type TReportAveragePaymentsByUserGroupParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  user_groups?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportAveragePaymentsByUserGroupData = {
  date: string;
  user_groups: Record<string, number>;
  user_groups_previous?: Record<string, number>;
  user_groups_previous_year?: Record<string, number>;
};

export type TReportAveragePaymentsByUserGroupRes = {
  data: TReportAveragePaymentsByUserGroupData[];
  total_by_group: Record<string, number>;
  available_groups: string[];
};

// New API types for average payments by user group
export type TAveragePaymentsByUserGroupRequest = {
  start_date: string;
  end_date: string;
  grouping: ReportGroupBy | 'day' | 'week' | 'month' | 'quarter' | 'year';
  user_groups: number[];
};

export type TAveragePaymentsByUserGroupPeriod = {
  period: string;
  user_groups: Record<string, number>;
};

export type TAveragePaymentsByUserGroupResponse = {
  grouping: string;
  start_date: string;
  end_date: string;
  selected_group_ids: number[];
  periods: TAveragePaymentsByUserGroupPeriod[];
  totals: Record<string, number>;
};

export type TReportAverageLifetimeValueCohortAnalysisParams = {
  grouping: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
};

export type TReportAverageLifetimeValueCohortAnalysisData = {
  cohort_period: string;
  average_lifetime_value: number;
};

export type TReportAverageLifetimeValueCohortAnalysisRes = {
  data: TReportAverageLifetimeValueCohortAnalysisData[];
  grouping: ReportGroupBy;
  cohort_size: number;
  total_average_ltv: number;
};

export type TReportCancelFeedbackParams = {
  period: ReportPeriod;
  start_date?: string | null;
  end_date?: string | null;
};

export type TReportCancelFeedbackData = {
  total_cancel_feedback: number;
  reason: string;
};

export type TReportCancelFeedbackRes = {
  data: TReportCancelFeedbackData[];
};

export type TReportChurnRateParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  products?: (string | number)[];
  compare_to_previous_period?: boolean;
};

export type TReportChurnRateData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
};

export type TReportChurnRateRes = {
  data: TReportChurnRateData[];
  total_by_product: Record<string, number>;
  available_products: string[];
};

export type TReportCountOfCancellationsParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  products?: string[];
  compare_to_previous_period?: boolean;
};

export type TReportCountOfCancellationsData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
};

export type TReportCountOfCancellationsRes = {
  data: TReportCountOfCancellationsData[];
  total_by_product: Record<string, number>;
  available_products: string[];
};

export type TCommonReportWithProductsData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TCommonReportWithProductsRes = {
  data: TCommonReportWithProductsData[];
};

export type TCommonReportWithPaymentSystemsData = {
  date: string;
  payment_systems: Record<string, number>;
  payment_systems_previous?: Record<string, number>;
  payment_systems_previous_year?: Record<string, number>;
};

export type TCommonReportWithPaymentSystemsRes = {
  data: TCommonReportWithPaymentSystemsData[];
};

export type TReportCountOfUserSignupsData = {
  date: string;
  count: number;
  count_previous?: number;
  count_previous_year?: number;
};

export type TReportCountOfUserSignupsRes = {
  data: TReportCountOfUserSignupsData[];
  summary: {
    count: number;
    count_previous: number;
    count_previous_year: number;
  };
};

export type TReportCountOfSignupByCountryParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  countries?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportCountOfSignupByCountryData = {
  date: string;
  countries: Record<string, number>;
  countries_previous?: Record<string, number>;
  countries_previous_year?: Record<string, number>;
};

export type TReportCountOfSignupByCountryRes = {
  data: TReportCountOfSignupByCountryData[];
};

export type TReportCountOfProductPurchaseByPaymentSystemParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  payment_systems?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportCountOfProductPurchaseByPaymentSystemData = {
  date: string;
  payment_systems: Record<string, number>;
  payment_systems_previous?: Record<string, number>;
  payment_systems_previous_year?: Record<string, number>;
};

export type TReportCountOfProductPurchaseByPaymentSystemRes = {
  data: TReportCountOfProductPurchaseByPaymentSystemData[];
  total_by_payment_system: Record<string, number>;
  available_payment_systems: string[];
};

export type TReportConversionRateParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
};

export type TReportConversionRateData = {
  payment: string;
  conversion_rate: number;
};

export type TReportConversionRateRes = {
  data: TReportConversionRateData[];
  total: number;
  categories: string[];
};

export type TReportConversionRateByPaymentSystemBreakdownParams = {
  start_date?: string | null;
  end_date?: string | null;
};

export type TReportConversionRateByPaymentSystemBreakdownData = {
  payment_system: string | null;
  conversion_rate: number;
};

export type TReportConversionRateByPaymentSystemBreakdownRes =
  TReportConversionRateByPaymentSystemBreakdownData[];

export type TEvalAccountByDatePaPayoutNewJodyTable = {
  id?: string;
  packageName?: string;
  evalAccounts?: number;
  uniqueEvalUsers?: number;
  qualifiedToPa?: number;
  uniquePaUser?: number;
  paStillActive?: number;
  uniqueActivePaUsers?: number;
  receivedPayouts?: number;
  payoutTotal?: number;
  paToPayout?: number;
};
export type TPayoutsTotalsReportTable = {
  id?: string;
  packages?: string;
  totalPaAccounts?: number;
  totalPayouts?: number;
  payoutUniqueAccounts?: number;
  payoutUniqueUsers?: number;
  totalPayout?: number;
  accountsWithPayout1?: number;
  accountsWithPayout2?: number;
  accountsWithPayout3?: number;
  accountsWithPayout4?: number;
  accountsWithPayout5?: number;
  accountsWithPayout6?: number;
  accountsWithPayout7?: number;
};

export type TPayoutsAmountReportTable = {
  id?: string;
  month?: string;
  amount?: number;
};

export type TPayoutsPaAnalysisByDateTable = {
  package: string;
  total_pa_accounts: number;
  total_payouts: number;
  accounts_30_days: number;
  users_30_days: number;
  avg_pnl_30_days: string;
  accounts_60_days: number;
  users_60_days: number;
  avg_pnl_60_days: string;
  accounts_90_days: number;
  users_90_days: number;
  avg_pnl_90_days: string;
  accounts_120_days: number;
  users_120_days: number;
  avg_pnl_120_days: string;
  accounts_150_days: number;
  users_150_days: number;
  avg_pnl_150_days: string;
  payout_per_month: string;
  payout_max_account: string;
  total_accounts: number;
  accounts_to_payout_ratio: string;
};

export type TPaPayoutAnalysisTotals = {
  package: string;
  total_pa_accounts: number;
  total_payouts: number;
  accounts_30_days: number;
  users_30_days: number;
  accounts_60_days: number;
  users_60_days: number;
  accounts_90_days: number;
  users_90_days: number;
  accounts_120_days: number;
  users_120_days: number;
  accounts_150_days: number;
  users_150_days: number;
  payout_per_month_total: string;
  payout_max_account_total: string;
  total_accounts: number;
  accounts_to_payout_ratio: string;
};

export type TPaPayoutAnalysisPercentOfTotal = {
  percent_30_days: string;
  percent_60_days: string;
  percent_90_days: string;
  percent_120_days: string;
  percent_150_days: string;
};

export type TPaPayoutAnalysisSummaryRow = {
  description: string;
  amount: number;
  percent: string;
  avg_days: number;
  max_days: number;
};

export type TPaPayoutAnalysisParams = {
  from_date: string;
  to_date: string;
};

export type TPaPayoutAnalysisRes = {
  detail_rows: TPayoutsPaAnalysisByDateTable[];
  totals: TPaPayoutAnalysisTotals;
  percent_of_total: TPaPayoutAnalysisPercentOfTotal;
  summary_rows: TPaPayoutAnalysisSummaryRow[];
  generated_at: string;
};

export type TPaTradersWithExcessiveDailyProfitTable = {
  id?: string;
  packageName?: string;
  evalAccounts?: number;
  uniqueEvalUsers?: number;
  qualifiedToPa?: number;
  uniquePaUsers?: number;
  paStillActive?: number;
  uniqueActivePaUsers?: number;
  receivedPayouts?: number;
  payoutsTotal?: number;
  paToPayout?: number;
};

export type TActiveLifetimePAParams = {
  not_traded_days?: number;
  in_period_days?: number;
  page?: number;
  page_size?: number;
};

export type TActiveLifetimePAData = {
  account_type: string;
  full_name: string;
  account_id: number;
  username: string;
  rithmic_account_name: string;
  package_name: string;
  valid_from: string;
  valid_to: string;
  user_id: number;
  trading_days_in_period: number;
  total_trading_days: number;
  package_id: number;
  altv: string;
  account_balance: string;
  account_balance_date: string;
  total_account: number;
};

export type TActiveLifetimePARes = {
  data: TActiveLifetimePAData[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
  generated_at: string;
  title: string;
};

export type TPaDailyPlHitting30PercentOfTargetTable = {
  id?: string;
  userName?: string;
  userId?: string;
  accountNo?: string;
  accountDes?: string;
  package?: string;
  fullName?: string;
  status?: string;
  balance?: number;
  notes?: string;
  autoLiquidatedOn?: string;
  totalProfit?: number;
  maxAllowedDailyProfit?: number;
  highestDailyProfit?: number;
  highestDailyProfitPercent?: number;
  tradingDays?: number;
  lastPayout?: string;
  accountPayoutCount?: number;
  accountPayoutTotal?: number;
  customerPayoutCount?: number;
  customerPayoutTotal?: number;
};

export type TReportOneDayProfitParams = {
  start_date?: string; // Format: YYYY-MM-DD
  live_accounts?: boolean;
  profit_percent?: number;
  min_trading_days?: number;
  page?: number;
  per_page?: number;
};

export type TReportOneDayProfitData = {
  account_id: number;
  account_name: string;
  package_name: string;
  user_id: number;
  user_name: string;
  full_name: string;
  status?: string;
  balance?: string;
  notes?: string;
  auto_liquidated_on?: string;
  account_trading_days: number;
  total_profit: string;
  max_day_profit_allowed: string;
  best_day_profit: string;
  profit_percent: string;
  last_payout_date: string;
  account_payouts: number;
  account_payout_total: string;
  user_payouts: number;
  user_payout_total: string;
};

export type TReportOneDayProfitRes = {
  data: TReportOneDayProfitData[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
  generated_at: string;
  title: string;
};

export type TViewAccountStatsRes = {
  account_id: number;
  account_no: string | null;
  trading_days: number;
  status: string | null;
  balance: string | number | null;
  notes: string | null;
  auto_liquidated_on: string | null;
  daily_trade_volumes_and_contracts: {
    date: string;
    wining: number;
    losing: number;
    contracts: number;
  }[];
  daily_profit_loss_and_balance: {
    date: string;
    account_balance: string | number;
    net_pl: string | number;
  }[];
};

export type TReportProfitableTradersTable = {
  account_id: number;
  account_name: string;
  user_id: number;
  user: string;
  package: string;
  trading_days: number;
  win_percent: number;
  balance: string;
  updated: string;
  altv: string;
  total_payout_amount: string;
  last_payout: string;
  total_payouts: number;
  total_payout_amount_user: string;
};

export type TReportProfitableTradersRes = {
  data: TReportProfitableTradersTable[];
  total_accounts: number;
  generated_at: string;
};

export type TReportProfitableTradersV2Package = 'all' | 'super' | 'std';

export type TReportProfitableTradersV2Params = {
  profit_required: number;
  package: TReportProfitableTradersV2Package;
};

export type TReportProfitableTradersV2Res = TReportProfitableTradersRes;

export enum ReportType {
  OLD_AFFILIATE_CLICKS = 'old_affiliate_clicks',
  OLD_AFFILIATE_COMMISSIONS_BY_USER = 'old_affiliate_commissions_BY_USER',
  OLD_AFFILIATE_COMMISSIONS = 'old_affiliate_commissions',
  OLD_AFFILIATE_COMMISSIONS_BY_PRODUCTS = 'old_affiliate_commissions_by_products',
  OLD_AFFILIATE_PAYOUT_AMOUNT = 'old_affiliate_payout_amount',
  OLD_AFFILIATE_SALES_NUMBER = 'old_affiliate_sales_number',
  NEW_AFFILIATE_CLICKS = 'new_affiliate_clicks',
  NEW_AFFILIATE_COMMISSIONS_BY_USER = 'new_affiliate_commissions_BY_USER',
  NEW_AFFILIATE_COMMISSIONS = 'new_affiliate_commissions',
  NEW_AFFILIATE_COMMISSIONS_BY_PRODUCTS = 'new_affiliate_commissions_by_products',
  NEW_AFFILIATE_PAYOUT_AMOUNT = 'new_affiliate_payout_amount',
  NEW_AFFILIATE_SALES_NUMBER = 'new_affiliate_sales_number',
  USERS_NEW_USERS_BY_MONTH = 'users_new_users_by_month',
  USERS_ACTIVE_USERS_WITH_NO_ACTIVE_ACCOUNTS = 'users_active_users_with_no_active_accounts',
  USERS_ACTIVE_USERS_EXPIRING_BEFORE_ACCOUNT_EXPIRY = 'users_active_users_expiring_before_account_expiry',
  USERS_ACTIVE_USERS_WITH_TOP_OF_BOOK = 'users_active_users_with_top_of_book',
  USERS_ACTIVE_USERS_WITH_EXTRA_SESSION = 'users_active_users_with_extra_session',
  USERS_ACTIVE_USERS_WITH_EUREX = 'users_active_users_with_eurex',
  USERS_CHURN_RATE_BY_DATE = 'users_churn_rate_by_date',
  USERS_BLACKLISTED_USERS = 'users_blacklisted_users',
  EVAL_ACCOUNT_BY_DATE_PA_PAYOUT_NEW_JODY = 'eval_account_by_date_pa_payout_new_jody',
  EVAL_NEW_RENEWAL_BY_MONTH = 'eval_new_renewal_by_month',
  PA_DAILY_PL = 'pa_daily_pl',
  EVAL_EVAL_ANALYSIS_BY_CREATION_DATE = 'eval_eval_analysis_by_creation_date',
  BUNDLES_PA_ACCOUNTS = 'bundles_pa_accounts',
  PA_DAILY_PL_HITTING_30_PERCENT_OF_TARGET = 'pa_daily_pl_hitting_30_percent_of_target',
  PA_NEW_ACCOUNTS_BY_DATE = 'pa_new_accounts_by_date',
  PA_ACTIVE_LIFETIME_PA = 'pa_active_lifetime_pa',
  PA_PROFITABLE_TRADERS = 'pa_profitable_traders',
  PA_PNL_ANALYSIS = 'pa_pnl_analysis',
  PA_RESETS = 'pa_resets',
  PA_TYPE_ACTIVE_PAYOUTS_ADDONS_V2 = 'pa_type_active_payouts_addons_v2',
  PA_NEW_RENEWAL_RESET_BY_MONTH = 'pa_new_renewal_reset_by_month',
  PA_PROFITABLE_TRADERS_V2 = 'pa_profitable_traders_v2',
  PAYOUTS_TOTALS_REPORT = 'payouts_totals_report',
  PAYOUTS_PA_ANALYSIS_BY_DATE = 'payouts_pa_analysis_by_date',
  EXPRESS_ACCOUNT_PA_REFUNDS = 'express_account_pa_refunds',
  LEADERBOARD = 'leaderboard',
  STAFF_STAFF_FREEBIES_BY_MONTH = 'staff_staff_freebies_by_month',
  ACTIVE_USERS_BY_PERIOD = 'active_users_by_period',
  ACTIVE_USERS_BY_PRODUCT_CATEGORIES = 'active_users_by_product_categories',
  ACTIVE_USERS_BY_PRODUCTS = 'active_users_by_products',
  AMOUNT_OF_REBILLS = 'amount_of_rebills',
  AVERAGE_AMOUNT_BY_PRODUCTS_BREAKDOWN = 'average_amount_by_products_breakdown',
  AVERAGE_LIFETIME_VALUE_COHORT_ANALYSIS = 'average_lifetime_value_cohort_analysis',
  AVERAGE_PAYMENTS_BY_USER_GROUP = 'average_payments_by_user_group',
  CANCEL_FEEDBACK = 'cancel_feedback',
  CHURN_RATE = 'churn_rate',
  CONVERSION_RATE_BY_PAYMENT_SYSTEM_BREAKDOWN = 'conversion_rate_by_payment_system_breakdown',
  COUNT_OF_CANCELLATIONS = 'count_of_cancellations',
  COUNT_OF_PENDING_INVOICES = 'count_of_pending_invoices',
  COUNT_OF_SIGNUPS_BY_COUNTRY = 'count_of_signups_by_country',
  COUNT_OF_PRODUCT_PURCHASE = 'count_of_product_purchase',
  COUNT_OF_PRODUCT_PURCHASE_BY_PAYMENT_SYSTEM_BREAKDOWN = 'count_of_product_purchase_by_payment_system_breakdown',
  COUNT_OF_USER_SIGNUPS = 'count_of_user_signups',
  CREDITS_SPENT_BY_PRODUCTS_BREAKDOWN = 'credits_spent_by_products_breakdown',
  DOWNLOADS_BY_FILES_BREAKDOWN = 'downloads_by_files_breakdown',
  INCOME_REPORT = 'income_report',
  INVOICES_BY_SIGNUP_FORM = 'invoices_by_signup_form',
  NUMBER_OF_PAYMENTS_BY_BILLING_PLAN = 'number_of_payments_by_billing_plan',
  NUMBER_OF_REBILLS = 'number_of_rebills',
  PAYMENTS_DISTRIBUTION_BY_CUSTOMER_COUNTRY = 'payments_distribution_by_customer_country',
  PAYMENTS_BY_CUSTOMER_CITY = 'payments_by_customer_city',
  PAYMENTS_BY_CUSTOMER_COUNTRY = 'payments_by_customer_country',
  PAYMENTS_BY_NEW_VS_EXISTING_MEMBERS = 'payments_by_new_vs_existing_members',
  PAYMENTS_BY_USED_COUPON = 'payments_by_used_coupon',
  PAYMENTS_BY_USER_GROUP = 'payments_by_user_group',
  PAYMENTS_BY_PAYMENT_SYSTEM_BREAKDOWN = 'payments_by_payment_system_breakdown',
  PAYMENTS_BY_PRODUCT_CATEGORIES_BREAKDOWN = 'payments_by_product_categories_breakdown',
  PAYMENTS_BY_PRODUCTS_BREAKDOWN = 'payments_by_products_breakdown',
  PAYMENTS_BY_SIGNUP_FORM_BREAKDOWN = 'payments_by_signup_form_breakdown',
  PAYMENTS_MINUS_REFUNDS_BY_CUSTOMER_COUNTRY = 'payments_minus_refunds_by_customer_country',
  PAYMENTS_MINUS_REFUNDS_BY_CUSTOMER_STATE = 'payments_minus_refunds_by_customer_state',
  PAYMENTS_VS_REFUNDS = 'payments_vs_refunds',
  PRODUCT_CONVERSION = 'product_conversion',
  REFUNDS_DISTRIBUTION_BY_CUSTOMER_COUNTRY = 'refunds_distribution_by_customer_country',
  REFUNDS_RATE = 'refunds_rate',
  REFUNDS_BY_CUSTOMER_COUNTRY = 'refunds_by_customer_country',
  REFUNDS_BY_USED_COUPON = 'refunds_by_used_coupon',
  REFUNDS_BY_PAYMENT_SYSTEM_BREAKDOWN = 'refunds_by_payment_system_breakdown',
  REFUNDS_BY_PRODUCT_CATEGORIES_BREAKDOWN = 'refunds_by_product_categories_breakdown',
  REFUNDS_BY_PRODUCTS_BREAKDOWN = 'refunds_by_products_breakdown',
  RETENTION_RATE = 'retention_rate',
  ROLLING_CONVERSION = 'rolling_conversion',
  TAX_REPORT = 'tax_report',
  TAX_BY_CUSTOMER_COUNTRY = 'tax_by_customer_country',
  TAX_BY_CUSTOMER_STATE = 'tax_by_customer_state',
  USER_DEMOGRAPHICS = 'user_demographics',
  USER_DISTRIBUTION_BY_CUSTOM_FIELD = 'user_distribution_by_custom_field',
  USER_DISTRIBUTION_BY_CUSTOM_FIELD_BY_DATE = 'user_distribution_by_custom_field_by_date',
  USER_LIFETIME = 'user_lifetime',
  USERS_BY_USER_GROUPS = 'users_by_user_groups',
  NEW_AVAL_ACCOUNT_BY_MONTH = 'new_aval_account_by_month',
  EVAL_ACCOUNT_BY_MONTH = 'eval_account_by_month',
}

export enum ReportPeriod {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  THIS_WEEK_SUN_SAT = 'this_week_sun_sat',
  THIS_WEEK_MON_SUN = 'this_week_mon_sun',
  LAST_7_DAYS = 'last_7_days',
  LAST_WEEK_SUN_SAT = 'last_week_sun_sat',
  LAST_WEEK_MON_SUN = 'last_week_mon_sun',
  LAST_BUSINESS_WEEK_MON_FRI = 'last_business_week_mon_fri',
  LAST_14_DAYS = 'last_14_days',
  THIS_MONTH = 'this_month',
  LAST_30_DAYS = 'last_30_days',
  LAST_MONTH = 'last_month',
  THIS_QUARTER = 'this_quarter',
  LAST_QUARTER = 'last_quarter',
  LAST_90_DAYS = 'last_90_days',
  LAST_6_MONTHS = 'last_6_months',
  LAST_12_MONTHS = 'last_12_months',
  LAST_CALENDAR_YEAR = 'last_calendar_year',
  THIS_YEAR = 'this_year',
  ALL_TIME = 'all_time',
  EXACT = 'exact',
}

export enum ReportCategories {
  ACTIVE_LEELOO_TRADERS_ONLY_SALE = 'active_leeloo_traders_only_sale',
  PRODUCTS_WITH_COMMISSION = 'products_with_commission',
  TURBOS = 'turbos',
  ALL_MONTHLY_EVALUATIONS = 'all_monthly_evaluations',
  ADD_ONS = 'add_ons',
  FULL_MEMBERSHIP = 'full_membership',
}

export enum ReportGroupBy {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  QUARTER = 'quarter',
  YEAR = 'year',
}

export enum ReportProductType {
  RITHMIC_LEELOO_EXPRESS_3 = '(3) Rithmic | Leeloo Express | 14 Days | 12 Contract | No Refunds',
  RITHMIC_ASPIRE_5 = '(5) Rithmic | Aspire | 3ct, 25k | Trailing Drawdown',
  RITHMIC_LAUNCH_6 = '(6) Rithmic | Launch | 8ct, 50k | Trailing Drawdown',
  RITHMIC_CLIMB_7 = '(7) Rithmic | Climb | 12ct, 100k | Trailing Drawdown',
  RITHMIC_CRUISE_8 = '(8) Rithmic | Cruise | 15ct, 150k | Trailing Drawdown',
  RITHMIC_BURST_9 = '(9) Rithmic | Burst | 25ct, 250k | Trailing Drawdown',
  RITHMIC_EXPLODE_10 = '(10) Rithmic | Explode | 30ct, 300k | Trailing Drawdown',
  RITHMIC_GLIDE_17 = '(17) Rithmic | Glide | Micro | Static Drawdown',
  SOAR_33 = '(33) Soar | Static Max Loss',
  RITHMIC_PERFORMANCE_ACCOUNT_16 = '(16) Rithmic | Performance Account',
  LEELOO_EXPRESS_ADD_ONE_DAY_19 = '(19) Leeloo Express ADD ONE Day',
  PRACTICE_ACCOUNT_32 = '(32) Practice Account | 3ct',
  THE_LEELOO_CLASS_11 = '(11) The LEELOO Class | Our Signature Scalp Trades',
  LEELOO_TREND_INDICATOR_12 = '(12) LEELOO Trend Indicator',
  RITHMIC_RESET_FEE_13 = '(13) Rithmic | Reset Fee (NOT FOR PAs)',
  NON_PRO_EUREX_DATA_14 = '(14) Non-Pro EUREX DATA',
  TEST_PRODUCT_15 = '(15) Test Product',
  RITHMIC_EXTRA_SESSION_COUNT_22 = '(22) Rithmic | Extra Session Count',
  RITHMIC_MARKET_DEPTH_ALL_4_EXCHANGE_BUNDLE_23 = '(23) Rithmic | Market Depth | All 4 Exchange Bundle',
  RITHMIC_CME_MARKET_DEPTH_24 = '(24) Rithmic | CME Market Depth',
  RITHMIC_CBOT_MARKET_DEPTH_25 = '(25) Rithmic | CBOT Market Depth',
  RITHMIC_COMEX_MARKET_DEPTH_26 = '(26) Rithmic | COMEX Market Depth',
  RITHMIC_NYMEX_MARKET_DEPTH_27 = '(27) Rithmic | NYMEX Market Depth',
  RITHMIC_PROFESSIONAL_USERS_MARKET_DATA_BUNDLE_37 = '(37) Rithmic | Professional Users | Market Data Bundle',
  RITHMIC_PROFESSIONAL_USERS_CME_DATA_38 = '(38) Rithmic | Professional Users | CME Data',
  AFFILIATES_39 = '(39) Affiliates',
  RITHMIC_RE_CONNECT_FEE_49 = '(49) Rithmic | Re-Connect Fee',
  RITHMIC_LEELOO_IGNITE_51 = '(51) Rithmic | Leeloo Ignite | 20 Days | 14ct, 100K | Trailing Drawdown',
  DEMO_ACCOUNTS_PACKAGE_OF_3_52 = '(52) Demo (DM) Accounts Package of 3 | $50k 8 contract Demo | No Refunds | Does NOT qualify for PA',
  COMPETITION_53 = '(53) (CM) Competition | $100k 3 Contracts | No Trailing Drawdown | No Refunds | Will NOT qualify for PA |',
  RITHMIC_PA_RESET_25K_INVESTOR_54 = '(54) Rithmic | PA Reset $25k Investor',
  RITHMIC_PA_RESET_50K_INVESTOR_55 = '(55) Rithmic | PA Reset $50k Investor',
  RITHMIC_PA_RESET_100K_INVESTOR_56 = '(56) Rithmic | PA Reset $100k Investor',
  RITHMIC_PA_RESET_150K_INVESTOR_57 = '(57) Rithmic | PA Reset $150k Investor',
  RITHMIC_PA_RESET_250K_INVESTOR_58 = '(58) Rithmic | PA Reset $250k Investor',
  RITHMIC_PA_RESET_300K_INVESTOR_59 = '(59) Rithmic | PA Reset $300k Investor',
  RITHMIC_PA_RESET_100K_ACCELERATOR_60 = '(60) Rithmic | PA Reset $100k Accelerator',
  LEELOO_MERCHANT_LISTING_61 = '(61) Leeloo Merchant Listing',
  PROFESSIONAL_EUREX_DATA_62 = '(62) Professional EUREX DATA',
  THESTOPHUNTER_CHALLENGE_63 = '(63) THESTOPHUNTER Challenge',
  IM_NOT_A_DOCTOR_TEE_SHIRT_65 = "(65) I'm not a Doctor Tee Shirt",
  RITHMIC_LB_BUNDLE_25K_ASPIRE_64 = '(64) Rithmic | LB Bundle $25,000 Aspire | 3 minis | 30 micros',
  RITHMIC_LB_BUNDLE_50K_LAUNCH_66 = '(66) Rithmic | LB Bundle $50,000 Launch |8 minis | 80 micros',
  RITHMIC_LB_BUNDLE_100K_CLIMB_67 = '(67) Rithmic | LB Bundle $100,000 Climb | 12 minis | 120 micros',
  RITHMIC_LB_BUNDLE_150K_CRUISE_68 = '(68) Rithmic | LB Bundle $150,000 Cruise | 15 minis | 150 micros',
  RITHMIC_LB_BUNDLE_250K_BURST_69 = '(69) Rithmic | LB Bundle $250,000 Burst | 25 minis | 250 micros',
  RITHMIC_LB_BUNDLE_300K_EXPLODE_70 = '(70) Rithmic | LB Bundle $300,000 Explode | 30 minis | 300 micros',
  RITHMIC_LEELOO_ENTRY_LEVEL_ASPIRE_71 = '(71) Rithmic | Leeloo Entry Level (LE) Aspire | 3ct, 25k | Trailing Drawdown',
  RITHMIC_LEELOO_ENTRY_LEVEL_LAUNCH_72 = '(72) Rithmic | Leeloo Entry Level (LE) Launch | 8ct, 50k | Trailing Drawdown',
  RITHMIC_LEELOO_ENTRY_LEVEL_CLIMB_73 = '(73) Rithmic | Leeloo Entry Level (LE) Climb | 12ct, 100k | Trailing Drawdown',
  RITHMIC_LEELOO_ENTRY_LEVEL_CRUISE_74 = '(74) Rithmic | Leeloo Entry Level (LE) Cruise | 15ct, 150k | Trailing Drawdown',
  RITHMIC_LEELOO_ENTRY_LEVEL_BURST_75 = '(75) Rithmic | Leeloo Entry Level (LE) Burst | 25ct, 250k | Trailing Drawdown',
  RITHMIC_LEELOO_ENTRY_LEVEL_EXPLODE_76 = '(76) Rithmic | Leeloo Entry Level (LE) Explode | 30ct, 300k | Trailing Drawdown',
  RITHMIC_LEELOO_ENTRY_LEVEL_GLIDE_78 = '(78) Rithmic | Leeloo Entry Level LE Glide | Micro | Static Drawdown',
  RITHMIC_TURBO_SPRINT_25K_79 = '(79) Rithmic | Turbo Sprint – $25K Account | 3 minis | 30 micros | Trailing Drawdown',
  RITHMIC_TURBO_PACE_50K_80 = '(80) Rithmic | Turbo Pace – $50K Account | 8 minis | 80 micros | Trailing Drawdown',
  RITHMIC_TURBO_POWER_100K_81 = '(81) Rithmic | Turbo Power – $100K Account | 12 minis | 120 micros | Trailing Drawdown',
  RITHMIC_PERFORMANCE_ACCOUNT_ONE_TIME_FEE_82 = '(82) Rithmic | Performance Account ONE TIME FEE',
  // Tradovate entries - Commented out
  // TRADOVATE_RESET_FEE_84 = '(84) Tradovate Reset Fee (NOT FOR PAs)',
  // TRADOVATE_MARKET_DEPTH_ALL_4_EXCHANGE_BUNDLE_85 = '(85) Tradeovate Market Depth | All 4 Exchange Bundle',
  // TRADOVATE_LEELOO_EXPRESS_86 = '(86) Tradovate Leeloo Express | 14 Days | 12 Contract | No Refunds',
  // TRADOVATE_GLIDE_83 = '(83) Tradovate Glide | Micro | Static Drawdown',
  // TRADOVATE_ASPIRE_87 = '(87) Tradovate Aspire | 3ct, 25k | Trailing Drawdown',
  // TRADOVATE_LAUNCH_88 = '(88) Tradovate Launch | 8ct, 50k | Trailing Drawdown',
  // TRADOVATE_CLIMB_89 = '(89) Tradovate Climb | 12ct, 100k | Trailing Drawdown',
  // TRADOVATE_CRUISE_90 = '(90) Tradovate Cruise | 15ct, 150k | Trailing Drawdown',
  // TRADOVATE_BURST_91 = '(91) Tradovate Burst | 25ct, 250k | Trailing Drawdown',
  // TRADOVATE_EXPLODE_92 = '(92) Tradovate Explode | 30ct, 300k | Trailing Drawdown',
  // TRADOVATE_LEELOO_IGNITE_93 = '(93) Tradovate Leeloo Ignite | 20 Days | 14ct, 100K | Trailing Drawdown',
  // TRADOVATE_PERFORMANCE_ACCOUNT_98 = '(98) Tradovate Performance Account',
  TEST_FOR_ADD_ONS_ASPIRE_99 = '(99) TEST for ADD ONS Aspire | 3ct, 25k | Trailing Drawdown',
  STOCKS_50K_100 = '(100) Stocks 50k | Trailing Drawdown $500',
  STOCKS_RESET_FEE_101 = '(101) Stocks Reset Fee (NOT FOR PAs) Stocks ONLY',
  RITHMIC_ADDON_ALLOW_PA_PAYOUT_EVERY_15_DAYS_102 = '(102) Rithmic | AddOn Allow PA payout request every 15 traded days',
  PROFESSIONAL_USERS_COMEX_DATA_103 = '(103) Professional Users | COMEX Data',
  RITHMIC_ADDON_QUALIFY_EVAL_IN_1_DAY_104 = '(104) Rithmic | AddOn Qualify Eval in as little as 1 day after you meet profit target (reduced from standard qualify requirement)',
  RITHMIC_ADDON_REDUCED_COST_LIFETIME_PA_119_105 = '(105) Rithmic | AddOn Get reduced cost lifetime PA on qualify for $119 instead of $250',
  RITHMIC_ADDON_ALLOW_EXTRA_500_PA_PAYOUT_106 = '(106) Rithmic | AddOn Allow extra $500 on PA payout (assuming balance allows this)',
  PERFORMANCE_ACCOUNT_EE_119_107 = '(107) Performance Account EE $119',
  TEST_TEST_OF_LB_BUNDLE_25K_LAUNCH_108 = '(108) TEST TEST of LB Bundle $25,000 Launch | 3 Contract | One qualifies',
  RITHMIC_ADDON_RESET_60_DISCOUNT_PRACTICE_ACCOUNT_109 = '(109) Rithmic | Addon Reset 60% discount on Practice Account',
  PERFORMANCE_ACCOUNT_EE_90_110 = '(110) Performance Account EE $90',
  RITHMIC_REDUCED_COST_LIFETIME_PA_90_111 = '(111) Rithmic | Get reduced cost lifetime PA on qualify for $90 instead of $250',
  RITHMIC_ADDON_ALLOW_PA_PAYOUT_EVERY_10_DAYS_112 = '(112) Rithmic | AddOn Allow PA payout request every 10 traded days',
  RITHMIC_ADD_ON_NO_30_RULE_50_RULE_113 = '(113) Rithmic | Add on No 30% Rule, instead is 50% Rule',
  RITHMIC_ELITE_PROGRAM_ACCOUNT_114 = '(114) Rithmic | Elite Program Account',
  RITHMIC_ADD_ON_NO_30_RULE_115 = '(115) Rithmic | Add On: No 30% Rule',
  RITHMIC_KICKSTART_116 = '(116) Rithmic | KickStart | 10ct, 75k | Trailing Drawdown',
  RITHMIC_ELITE_PROGRAM_ACCOUNT_RESET_1000_117 = '(117) Rithmic | Elite Program Account Reset $1,000',
  // DxFeed entries - Commented out
  // DXFEED_LEELOO_EXPRESS_119 = '(119) dxFeed Leeloo Express | 14 Days | 12 Contract | No Refunds',
  // DXFEED_ASPIRE_120 = '(120) dxFeed | Aspire | 3ct, 25k | Trailing Drawdown',
  // DXFEED_LAUNCH_121 = '(121) dxFeed | Launch | 8ct, 50k | Trailing Drawdown',
  // DXFEED_CLIMB_123 = '(123) dxFeed | Climb | 12ct, 100k | Trailing Drawdown',
  // DXFEED_CRUISE_124 = '(124) dxFeed | Cruise | 15ct, 150k | Trailing Drawdown',
  // DXFEED_BURST_125 = '(125) dxFeed | Burst | 25ct, 250k | Trailing Drawdown',
  // DXFEED_EXPLODE_126 = '(126) dxFeed | Explode | 30ct, 300k | Trailing Drawdown',
  // DXFEED_GLIDE_127 = '(127) dxFeed | Glide | Micro | Static Drawdown',
  // DXFEED_PERFORMANCE_ACCOUNT_128 = '(128) dxFeed | Performance Account',
  // DXFEED_RESET_FEE_129 = '(129) dxFeed | Reset Fee (NOT FOR PAs)',
  // DXFEED_EXTRA_SESSION_COUNT_130 = '(130) dxFeed | Extra Session Count',
  // DXFEED_MARKET_DEPTH_ALL_4_EXCHANGE_BUNDLE_131 = '(131) dxFeed | Market Depth | All 4 Exchange Bundle',
  // DXFEED_CME_MARKET_DEPTH_132 = '(132) dxFeed | CME Market Depth',
  // DXFEED_CBOT_MARKET_DEPTH_133 = '(133) dxFeed | CBOT Market Depth',
  // DXFEED_COMEX_MARKET_DEPTH_134 = '(134) dxFeed | COMEX Market Depth',
  // DXFEED_NYMEX_MARKET_DEPTH_135 = '(135) dxFeed | NYMEX Market Depth',
  // DXFEED_RE_CONNECT_FEE_136 = '(136) dxFeed | Re-Connect Fee',
  // DXFEED_LEELOO_IGNITE_137 = '(137) dxFeed | Leeloo Ignite | 20 Days | 14ct, 100K | Trailing Drawdown',
  // DXFEED_PA_RESET_25K_INVESTOR_138 = '(138) dxFeed | PA Reset $25k Investor',
  // DXFEED_PA_RESET_50K_INVESTOR_139 = '(139) dxFeed | PA Reset $50k Investor',
  // DXFEED_PA_RESET_100K_INVESTOR_140 = '(140) dxFeed | PA Reset $100k Investor',
  // DXFEED_PA_RESET_150K_INVESTOR_141 = '(141) dxFeed | PA Reset $150k Investor',
  // DXFEED_PA_RESET_250K_INVESTOR_142 = '(142) dxFeed | PA Reset $250k Investor',
  // DXFEED_PA_RESET_300K_INVESTOR_144 = '(144) dxFeed | PA Reset $300k Investor',
  // DXFEED_PA_RESET_100K_ACCELERATOR_145 = '(145) dxFeed | PA Reset $100k Accelerator',
  // DXFEED_LB_BUNDLE_25K_ASPIRE_146 = '(146) dxFeed | LB Bundle $25,000 Aspire | 3 minis | 30 micros',
  // DXFEED_LB_BUNDLE_50K_LAUNCH_147 = '(147) dxFeed | LB Bundle $50,000 Launch |8 minis | 80 micros',
  // DXFEED_LB_BUNDLE_100K_CLIMB_148 = '(148) dxFeed | LB Bundle $100,000 Climb | 12 minis | 120 micros',
  // DXFEED_LB_BUNDLE_150K_CRUISE_150 = '(150) dxFeed | LB Bundle $150,000 Cruise | 15 minis | 150 micros',
  // DXFEED_LB_BUNDLE_250K_BURST_151 = '(151) dxFeed | LB Bundle $250,000 Burst | 25 minis | 250 micros',
  // DXFEED_LB_BUNDLE_300K_EXPLODE_152 = '(152) dxFeed | LB Bundle $300,000 Explode | 30 minis | 300 micros',
  // DXFEED_LEELOO_ENTRY_LEVEL_ASPIRE_153 = '(153) dxFeed | Leeloo Entry Level (LE) Aspire | 3ct, 25k | Trailing Drawdown',
  // DXFEED_LEELOO_ENTRY_LEVEL_LAUNCH_154 = '(154) dxFeed | Leeloo Entry Level (LE) Launch | 8ct, 50k | Trailing Drawdown',
  // DXFEED_LEELOO_ENTRY_LEVEL_CLIMB_155 = '(155) dxFeed | Leeloo Entry Level (LE) Climb | 12ct, 100k | Trailing Drawdown',
  // DXFEED_LEELOO_ENTRY_LEVEL_CRUISE_156 = '(156) dxFeed | Leeloo Entry Level (LE) Cruise | 15ct, 150k | Trailing Drawdown',
  // DXFEED_LEELOO_ENTRY_LEVEL_BURST_157 = '(157) dxFeed | Leeloo Entry Level (LE) Burst | 25ct, 250k | Trailing Drawdown',
  // DXFEED_LEELOO_ENTRY_LEVEL_EXPLODE_159 = '(159) dxFeed | Leeloo Entry Level (LE) Explode | 30ct, 300k | Trailing Drawdown',
  // DXFEED_LEELOO_ENTRY_LEVEL_GLIDE_160 = '(160) dxFeed | Leeloo Entry Level LE Glide | Micro | Static Drawdown',
  // DXFEED_WK_WEEKLY_ASPIRE_161 = '(161) dxFeed | WK Weekly Aspire | 3ct, 25k | Trailing Drawdown',
  // DXFEED_WK_WEEKLY_LAUNCH_162 = '(162) dxFeed | WK Weekly Launch | 8ct, 50k | Trailing Drawdown',
  // DXFEED_WK_WEEKLY_ACCOUNT_163 = '(163) dxFeed | WK Weekly Account | 12ct, 100k | Trailing Drawdown',
  // DXFEED_PERFORMANCE_ACCOUNT_ONE_TIME_FEE_164 = '(164) dxFeed | Performance Account ONE TIME FEE',
  // DXFEED_ADDON_ALLOW_PA_PAYOUT_EVERY_15_DAYS_165 = '(165) dxFeed | AddOn Allow PA payout request every 15 traded days',
  // DXFEED_ADDON_QUALIFY_EVAL_IN_1_DAY_166 = '(166) dxFeed | AddOn Qualify Eval in as little as 1 day after you meet profit target (reduced from standard qualify requirement)',
  // DXFEED_ADDON_REDUCED_COST_LIFETIME_PA_119_168 = '(168) dxFeed | AddOn Get reduced cost lifetime PA on qualify for $119 instead of $250',
  // DXFEED_ADDON_ALLOW_EXTRA_500_PA_PAYOUT_169 = '(169) dxFeed | AddOn Allow extra $500 on PA payout (assuming balance allows this)',
  // DXFEED_ADDON_ALLOW_PA_PAYOUT_EVERY_10_DAYS_171 = '(171) dxFeed | AddOn Allow PA payout request every 10 traded days',
  // DXFEED_RESET_60_DISCOUNT_PRACTICE_ACCOUNT_172 = '(172) dxFeed | Reset 60% discount on Practice Account',
  // DXFEED_REDUCED_COST_LIFETIME_PA_90_173 = '(173) dxFeed | Get reduced cost lifetime PA on qualify for $90 instead of $250',
  // DXFEED_10CT_75K_174 = '(174) dxFeed | 10ct, 75k | Trailing Drawdown',
  // DXFEED_SUPER_PA_RESET_250_175 = '(175) dxFeed | Super PA Reset $250',
  // DXFEED_ADD_ON_NO_30_RULE_176 = '(176) dxFeed | Add On: No 30% Rule',
  // DXFEED_ADD_ON_NO_30_RULE_50_RULE_177 = '(177) dxFeed | Add on No 30% Rule, instead is 50% Rule',
  // DXFEED_SUPER_PA_PERFORMANCE_ACCOUNT_178 = '(178) dxFeed | Super PA Performance Account',
  // DXFEED_PROFESSIONAL_USERS_COMEX_DATA_180 = '(180) dxFeed | Professional Users | COMEX Data',
  // DXFEED_PROFESSIONAL_USERS_CME_DATA_181 = '(181) dxFeed | Professional Users | CME Data',
  // DXFEED_PROFESSIONAL_USERS_MARKET_DATA_BUNDLE_182 = '(182) dxFeed | Professional Users | Market Data Bundle',
  // Tradovate entries - Commented out
  // TRADOVATE_KICKSTART_183 = '(183) Tradovate | KickStart | 10ct, 75k | Trailing Drawdown',
  // TRADOVATE_PERFORMANCE_ACCOUNT_ONE_TIME_FEE_184 = '(184) Tradovate | Performance Account ONE TIME FEE',
  // TRADOVATE_RE_CONNECT_FEE_185 = '(185) Tradovate | Re-Connect Fee',
  // TRADOVATE_RESET_FEE_186 = '(186) Tradovate | Reset Fee (NOT FOR PAs)',
  // TRADOVATE_EXTRA_SESSION_COUNT_187 = '(187) Tradovate | Extra Session Count',
  // TRADOVATE_PERFORMANCE_ACCOUNT_188 = '(188) Tradovate | Performance Account',
  // TRADOVATE_MARKET_DEPTH_ALL_4_EXCHANGE_BUNDLE_189 = '(189) Tradovate | Market Depth | All 4 Exchange Bundle',
  // TRADOVATE_CME_MARKET_DEPTH_190 = '(190) Tradovate | CME Market Depth',
  // TRADOVATE_CBOT_MARKET_DEPTH_191 = '(191) Tradovate | CBOT Market Depth',
  // TRADOVATE_COMEX_MARKET_DEPTH_192 = '(192) Tradovate | COMEX Market Depth',
  // TRADOVATE_NYMEX_MARKET_DEPTH_193 = '(193) Tradovate | NYMEX Market Depth',
  // TRADOVATE_PA_RESET_25K_INVESTOR_194 = '(194) Tradovate | PA Reset $25k Investor',
  // TRADOVATE_PA_RESET_50K_INVESTOR_195 = '(195) Tradovate | PA Reset $50k Investor',
  // TRADOVATE_PA_RESET_100K_INVESTOR_196 = '(196) Tradovate | PA Reset $100k Investor',
  // TRADOVATE_PA_RESET_150K_INVESTOR_197 = '(197) Tradovate | PA Reset $150k Investor',
  // TRADOVATE_PA_RESET_250K_INVESTOR_198 = '(198) Tradovate | PA Reset $250k Investor',
  // TRADOVATE_PA_RESET_300K_INVESTOR_199 = '(199) Tradovate | PA Reset $300k Investor',
  // TRADOVATE_PA_RESET_100K_ACCELERATOR_200 = '(200) Tradovate | PA Reset $100k Accelerator',
  // TRADOVATE_LB_BUNDLE_25K_ASPIRE_201 = '(201) Tradovate | LB Bundle $25,000 Aspire | 3 minis | 30 micros',
  // TRADOVATE_LB_BUNDLE_50K_LAUNCH_202 = '(202) Tradovate | LB Bundle $50,000 Launch |8 minis | 80 micros',
  // TRADOVATE_LB_BUNDLE_100K_CLIMB_203 = '(203) Tradovate | LB Bundle $100,000 Climb | 12 minis | 120 micros',
  // TRADOVATE_LB_BUNDLE_150K_CRUISE_204 = '(204) Tradovate | LB Bundle $150,000 Cruise | 15 minis | 150 micros',
  // TRADOVATE_LB_BUNDLE_250K_BURST_205 = '(205) Tradovate | LB Bundle $250,000 Burst | 25 minis | 250 micros',
  // TRADOVATE_LB_BUNDLE_300K_EXPLODE_206 = '(206) Tradovate | LB Bundle $300,000 Explode | 30 minis | 300 micros',
  // TRADOVATE_LEELOO_ENTRY_LEVEL_ASPIRE_207 = '(207) Tradovate | Leeloo Entry Level (LE) Aspire | 3ct, 25k | Trailing Drawdown',
  // TRADOVATE_LEELOO_ENTRY_LEVEL_LAUNCH_208 = '(208) Tradovate | Leeloo Entry Level (LE) Launch | 8ct, 50k | Trailing Drawdown',
  // TRADOVATE_LEELOO_ENTRY_LEVEL_CLIMB_209 = '(209) Tradovate | Leeloo Entry Level (LE) Climb | 12ct, 100k | Trailing Drawdown',
  // TRADOVATE_LEELOO_ENTRY_LEVEL_CRUISE_210 = '(210) Tradovate | Leeloo Entry Level (LE) Cruise | 15ct, 150k | Trailing Drawdown',
  // TRADOVATE_LEELOO_ENTRY_LEVEL_BURST_211 = '(211) Tradovate | Leeloo Entry Level (LE) Burst | 25ct, 250k | Trailing Drawdown',
  // TRADOVATE_LEELOO_ENTRY_LEVEL_EXPLODE_212 = '(212) Tradovate | Leeloo Entry Level (LE) Explode | 30ct, 300k | Trailing Drawdown',
  // TRADOVATE_LEELOO_ENTRY_LEVEL_GLIDE_213 = '(213) Tradovate | Leeloo Entry Level LE Glide | Micro | Static Drawdown',
  // TRADOVATE_WK_WEEKLY_ASPIRE_214 = '(214) Tradovate | WK Weekly Aspire | 3ct, 25k | Trailing Drawdown',
  // TRADOVATE_WK_WEEKLY_LAUNCH_215 = '(215) Tradovate | WK Weekly Launch | 8ct, 50k | Trailing Drawdown',
  // TRADOVATE_WK_WEEKLY_ACCOUNT_216 = '(216) Tradovate | WK Weekly Account | 12ct, 100k | Trailing Drawdown',
  // TRADOVATE_PROFESSIONAL_USERS_COMEX_DATA_217 = '(217) Tradovate | Professional Users | COMEX Data',
  // TRADOVATE_PROFESSIONAL_USERS_CME_DATA_218 = '(218) Tradovate | Professional Users | CME Data',
  // TRADOVATE_PROFESSIONAL_USERS_MARKET_DATA_BUNDLE_219 = '(219) Tradovate | Professional Users | Market Data Bundle',
  // TRADOVATE_ADDON_ALLOW_PA_PAYOUT_EVERY_15_DAYS_220 = '(220) Tradovate | AddOn Allow PA payout request every 15 traded days',
  // TRADOVATE_ADDON_QUALIFY_EVAL_IN_1_DAY_221 = '(221) Tradovate | AddOn Qualify Eval in as little as 1 day after you meet profit target (reduced from standard qualify requirement)',
  // TRADOVATE_ADDON_REDUCED_COST_LIFETIME_PA_119_222 = '(222) Tradovate | AddOn Get reduced cost lifetime PA on qualify for $119 instead of $250',
  // TRADOVATE_ADDON_ALLOW_EXTRA_500_PA_PAYOUT_223 = '(223) Tradovate | AddOn Allow extra $500 on PA payout (assuming balance allows this)',
  // TRADOVATE_ADDON_ALLOW_PA_PAYOUT_EVERY_10_DAYS_224 = '(224) Tradovate | AddOn Allow PA payout request every 10 traded days',
  // TRADOVATE_RESET_60_DISCOUNT_PRACTICE_ACCOUNT_225 = '(225) Tradovate | Reset 60% discount on Practice Account',
  // TRADOVATE_REDUCED_COST_LIFETIME_PA_90_226 = '(226) Tradovate | Get reduced cost lifetime PA on qualify for $90 instead of $250',
  // TRADOVATE_ADD_ON_NO_30_RULE_227 = '(227) Tradovate | Add On: No 30% Rule',
  // TRADOVATE_ADD_ON_NO_30_RULE_50_RULE_228 = '(228) Tradovate | Add on No 30% Rule, instead is 50% Rule',
  // TRADOVATE_SUPER_PA_RESET_250_229 = '(229) Tradovate | Super PA Reset $250',
  // TRADOVATE_SUPER_PA_PERFORMANCE_ACCOUNT_230 = '(230) Tradovate | Super PA Performance Account',
  // TRADOVATE_PA_RESET_25K_INVESTOR_231 = '(231) Tradovate | PA Reset $25k Investor',
  // TRADOVATE_PA_RESET_50K_INVESTOR_232 = '(232) Tradovate | PA Reset $50k Investor',
  // TRADOVATE_PA_RESET_100K_INVESTOR_233 = '(233) Tradovate | PA Reset $100k Investor',
  // TRADOVATE_PA_RESET_150K_INVESTOR_234 = '(234) Tradovate | PA Reset $150k Investor',
  // TRADOVATE_PA_RESET_250K_INVESTOR_235 = '(235) Tradovate | PA Reset $250k Investor',
  // TRADOVATE_PA_RESET_300K_INVESTOR_236 = '(236) Tradovate | PA Reset $300k Investor',
  // TRADOVATE_PA_RESET_100K_ACCELERATOR_237 = '(237) Tradovate | PA Reset $100k Accelerator',
}

// Utility functions to convert enums to human-readable text
export const formatReportPeriod = (period: ReportPeriod): string => {
  const periodLabels: Record<ReportPeriod, string> = {
    [ReportPeriod.TODAY]: 'Today',
    [ReportPeriod.YESTERDAY]: 'Yesterday',
    [ReportPeriod.THIS_WEEK_SUN_SAT]: 'This Week (Sun-Sat)',
    [ReportPeriod.THIS_WEEK_MON_SUN]: 'This Week (Mon-Sun)',
    [ReportPeriod.LAST_7_DAYS]: 'Last 7 Days',
    [ReportPeriod.LAST_WEEK_SUN_SAT]: 'Last Week (Sun-Sat)',
    [ReportPeriod.LAST_WEEK_MON_SUN]: 'Last Week (Mon-Sun)',
    [ReportPeriod.LAST_BUSINESS_WEEK_MON_FRI]: 'Last Business Week (Mon-Fri)',
    [ReportPeriod.LAST_14_DAYS]: 'Last 14 Days',
    [ReportPeriod.THIS_MONTH]: 'This Month',
    [ReportPeriod.LAST_30_DAYS]: 'Last 30 Days',
    [ReportPeriod.LAST_MONTH]: 'Last Month',
    [ReportPeriod.THIS_QUARTER]: 'This Quarter',
    [ReportPeriod.LAST_QUARTER]: 'Last Quarter',
    [ReportPeriod.LAST_90_DAYS]: 'Last 90 Days',
    [ReportPeriod.LAST_6_MONTHS]: 'Last 6 Months',
    [ReportPeriod.LAST_12_MONTHS]: 'Last 12 Months',
    [ReportPeriod.LAST_CALENDAR_YEAR]: 'Last Calendar Year',
    [ReportPeriod.THIS_YEAR]: 'This Year',
    [ReportPeriod.ALL_TIME]: 'All Time',
    [ReportPeriod.EXACT]: 'Exact',
  };

  return periodLabels[period];
};

export const formatReportCategory = (category: ReportCategories): string => {
  const categoryLabels: Record<ReportCategories, string> = {
    [ReportCategories.ACTIVE_LEELOO_TRADERS_ONLY_SALE]:
      'Active Leeloo Traders Only (Sale)',
    [ReportCategories.PRODUCTS_WITH_COMMISSION]: 'Products with Commission',
    [ReportCategories.TURBOS]: 'Turbos',
    [ReportCategories.ALL_MONTHLY_EVALUATIONS]: 'All Monthly Evaluations',
    [ReportCategories.ADD_ONS]: 'Add-ons',
    [ReportCategories.FULL_MEMBERSHIP]: 'Full Membership',
  };

  return categoryLabels[category];
};

export const formatReportGroupBy = (groupBy: ReportGroupBy): string => {
  const groupByLabels: Record<ReportGroupBy, string> = {
    [ReportGroupBy.DAY]: 'Day',
    [ReportGroupBy.WEEK]: 'Week',
    [ReportGroupBy.MONTH]: 'Month',
    [ReportGroupBy.QUARTER]: 'Quarter',
    [ReportGroupBy.YEAR]: 'Year',
  };

  return groupByLabels[groupBy];
};

// Generate options from enums
export const getPeriodOptions = (): TOption[] => {
  return Object.values(ReportPeriod).map((period) => ({
    label: formatReportPeriod(period),
    value: period,
  }));
};

export const getCategoryOptions = (): TOption[] => {
  return [
    {
      label: 'Practice Account',
      value: ProductTradingAccountTypes.PRACTICE_ACCOUNT,
    },
    {
      label: 'Straight to PA',
      value: ProductTradingAccountTypes.STRAIGHT_TO_PA,
    },
    {
      label: 'Specialty Account',
      value: ProductTradingAccountTypes.SPECIALTY_ACCOUNT,
    },
    {
      label: 'Add-ons',
      value: 'add_ons',
    },
    {
      label: 'All Monthly Evaluations',
      value: 'all_monthly_evaluations',
    },
  ];
};

export const getGroupByOptions = (): TOption[] => {
  return Object.values(ReportGroupBy).map((groupBy) => ({
    label: formatReportGroupBy(groupBy),
    value: groupBy,
  }));
};

export const getProductTypeOptions = (): TOption[] => {
  return Object.values(ReportProductType).map((product) => ({
    label: product,
    value: product,
  }));
};

export type TReportIncomeParams = {
  start_date?: string | null;
  end_date?: string | null;
  grouping?: string;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportIncomeData = {
  date: string;
  payment_amount: number;
  payment_amount_previous?: number;
  payment_amount_previous_year?: number;
  total_refunds: number;
  net_income: number;
};

export type TReportIncomeRes = TReportIncomeData[];

export type TReportDownloadByFilesBreakdownParams = {
  start_date?: string | null;
  end_date?: string | null;
  grouping?: ReportGroupBy;
  file_ids?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportDownloadByFilesBreakdownData = {
  date: string;
  files: Record<string, number>;
  files_previous?: Record<string, number>;
  files_previous_year?: Record<string, number>;
};

export type TReportDownloadByFilesBreakdownRes = {
  data: TReportDownloadByFilesBreakdownData[];
};

export type TReportDownloadByFilesBreakdownFileOption = {
  file_id: number;
  file_name: string;
};

export type TReportCreditsSpentByProductsBreakdownParams = {
  grouping?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  product_ids?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportCreditsSpentByProductsBreakdownData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TReportCreditsSpentByProductsBreakdownRes = {
  data: TReportCreditsSpentByProductsBreakdownData[];
};

export type TReportCountOfUserSignupsIncludePendingParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportCountOfUserSignupsIncludePendingData = {
  date: string;
  count_of_signups: number;
  count_of_signups_previous?: number;
  count_of_signups_previous_year?: number;
};

export type TReportCountOfUserSignupsIncludePendingRes = {
  data: TReportCountOfUserSignupsIncludePendingData[];
  total: number;
};

export type TReportInvoicesBySignupFormParams = {
  grouping?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  saved_form_ids?: number[];
  invoice_status?: 'all' | 'pending' | 'completed';
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportInvoicesBySignupFormData = {
  date: string;
  signup_forms: Record<string, number>;
  signup_forms_previous?: Record<string, number>;
  signup_forms_previous_year?: Record<string, number>;
};

export type TReportInvoicesBySignupFormRes = {
  data: TReportInvoicesBySignupFormData[];
};

export type TReportInvoicesBySignupFormOption = {
  saved_form_id: number;
  title: string;
};

export type TReportNumberOfPaymentsByBillingPlanParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  products?: ReportProductType[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportNumberOfPaymentsByBillingPlanData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TReportNumberOfPaymentsByBillingPlanRes = {
  data: TReportNumberOfPaymentsByBillingPlanData[];
  total_by_product: Record<string, number>;
  available_products: string[];
};

export type TReportPaymentsAmountByPaymentSystemParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
  payment_systems?: string[];
};

export type TReportPaymentsAmountByPaymentSystemData = {
  date: string;
  payment_systems: Record<string, number>;
  payment_systems_previous?: Record<string, number>;
  payment_systems_previous_year?: Record<string, number>;
};

export type TReportPaymentsAmountByPaymentSystemRes = {
  data: TReportPaymentsAmountByPaymentSystemData[];
  total_by_payment_system: Record<string, number>;
  available_payment_systems: string[];
};

export type TReportPaymentByNewVsExistingUsersParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentByNewVsExistingUsersData = {
  date: string;
  payments: Record<string, number>;
  payments_previous?: Record<string, number>;
  payments_previous_year?: Record<string, number>;
};

export type TReportPaymentByNewVsExistingUsersRes = {
  data: TReportPaymentByNewVsExistingUsersData[];
};

export type TReportPaymentByUsedCouponParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  coupon_batch: number;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentByUsedCouponData = {
  date: string;
  total_payment: number;
  total_payment_previous?: number;
  total_previous_year?: number;
};

export type TReportPaymentByUsedCouponRes = {
  data: TReportPaymentByUsedCouponData[];
};

export type TReportPaymentByUserGroupParams =
  TReportCommonPayloadWithGrouping & {
    user_groups?: number[];
  };

export type TReportPaymentByUserGroupOption = {
  user_group_id: number;
  user_group_name: string;
};

export type TReportPaymentByUserGroupData = {
  date: string;
  user_groups: Record<string, number>;
  user_groups_previous?: Record<string, number>;
  user_groups_previous_year?: Record<string, number>;
};

export type TReportPaymentByUserGroupRes = {
  data: TReportPaymentByUserGroupData[];
};

export type TReportPaymentsByPaymentSystemBreakdownParams = {
  grouping: string;
  start_date: string | null;
  end_date: string | null;
  payment_systems?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentsByPaymentSystemBreakdownData = {
  date: string;
  payment_systems: Record<string, number>;
  payment_systems_previous?: Record<string, number>;
  payment_systems_previous_year?: Record<string, number>;
};

export type TReportPaymentsByPaymentSystemBreakdownRes = {
  data: TReportPaymentsByPaymentSystemBreakdownData[];
};

export type TReportRefundsByPaymentSystemBreakdownParams = {
  group: string;
  start_date: string | null;
  end_date: string | null;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportRefundsByPaymentSystemBreakdownData = {
  date: string;
  payment_systems: Record<string, number>;
  payment_systems_previous?: Record<string, number>;
  payment_systems_previous_year?: Record<string, number>;
};

export type TReportRefundsByPaymentSystemBreakdownRes = {
  data: TReportRefundsByPaymentSystemBreakdownData[];
};

export type TReportPaymentsByProductCategoriesBreakdownParams = {
  group: ReportGroupBy;
  start_date: string | null;
  end_date: string | null;
  product_category_ids?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentsByProductCategoriesBreakdownData = {
  date: string;
  product_categories: Record<string, number>;
  product_categories_previous?: Record<string, number>;
  product_categories_previous_year?: Record<string, number>;
};

export type TReportPaymentsByProductCategoriesBreakdownRes = {
  data: TReportPaymentsByProductCategoriesBreakdownData[];
};

export type TReportPaymentsByProductCategoriesBreakdownOption = {
  product_category_id: number;
  product_category_name: string;
};

export type TReportRefundsByProductCategoriesBreakdownParams = {
  group: ReportGroupBy;
  start_date: string | null;
  end_date: string | null;
  product_category?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportRefundsByProductCategoriesBreakdownData = {
  date: string;
  product_categories: Record<string, number>;
  product_categories_previous?: Record<string, number>;
  product_categories_previous_year?: Record<string, number>;
};

export type TReportRefundsByProductCategoriesBreakdownRes = {
  data: TReportRefundsByProductCategoriesBreakdownData[];
  total_by_product_category: Record<string, number>;
  available_product_categories: string[];
};

export type TReportRefundsByProductCategoriesBreakdownOption = {
  product_category_id: number;
  product_category_name: string;
};

export type TReportPaymentsByProductsBreakdownParams = {
  period: ReportPeriod;
  group_by?: ReportGroupBy;
  start_date?: string | null;
  end_date?: string | null;
  products?: string[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentsByProductsBreakdownData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TReportPaymentsByProductsBreakdownRes = {
  data: TReportPaymentsByProductsBreakdownData[];
  total_by_product: Record<string, number>;
  available_products: string[];
};

export type TReportRefundsByProductsBreakdownParams = {
  group: ReportGroupBy;
  start_date: string | null;
  end_date: string | null;
  products?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportRefundsByProductsBreakdownData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TReportRefundsByProductsBreakdownRes = {
  data: TReportRefundsByProductsBreakdownData[];
};

export type TReportPaymentsBySignupFormBreakdownParams = {
  start_date: string | null;
  end_date: string | null;
  group: ReportGroupBy;
  saved_form_ids?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentsBySignupFormBreakdownData = {
  date: string;
  signup_forms: Record<string, number>;
  signup_forms_previous?: Record<string, number>;
  signup_forms_previous_year?: Record<string, number>;
};

export type TReportPaymentsBySignupFormBreakdownRes = {
  data: TReportPaymentsBySignupFormBreakdownData[];
  total_by_signup_form: Record<string, number>;
  available_signup_forms: string[];
};

export type TReportPaymentsMinusRefundsByCustomerCountryParams = {
  start_date: string | null;
  end_date: string | null;
  group: ReportGroupBy;
  product_ids?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentsMinusRefundsByCustomerCountryData = {
  date: string;
  countries: Record<string, number>;
  countries_previous?: Record<string, number>;
  countries_previous_year?: Record<string, number>;
};

export type TReportPaymentsMinusRefundsByCustomerCountryRes = {
  data: TReportPaymentsMinusRefundsByCustomerCountryData[];
};

export type TReportPaymentsMinusRefundsByCustomerStateParams = {
  country: string;
  start_date: string | null;
  end_date: string | null;
  group: ReportGroupBy;
  product_ids: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentsMinusRefundsByCustomerStateData = {
  date: string;
  states: Record<string, number>;
  states_previous?: Record<string, number>;
  states_previous_year?: Record<string, number>;
};

export type TReportPaymentsMinusRefundsByCustomerStateRes = {
  data: TReportPaymentsMinusRefundsByCustomerStateData[];
};

export type TReportPaymentsVsRefundsParams = {
  start_date: string | null;
  end_date: string | null;
  grouping: string;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportPaymentsVsRefundsData = {
  date: string;
  income: number;
  payment: number;
  refund: number;
  chargeback: number;
  income_previous?: number;
  payment_previous?: number;
  refund_previous?: number;
  chargeback_previous?: number;
  income_previous_year?: number;
  payment_previous_year?: number;
  refund_previous_year?: number;
  chargeback_previous_year?: number;
};

export type TReportPaymentsVsRefundsRes = {
  data: TReportPaymentsVsRefundsData[];
};

export type TReportProductConversionParams = {
  start_date: string | null;
  end_date: string | null;
  grouping?: ReportGroupBy;
  group?: ReportGroupBy;
  product_a: number;
  product_b: number;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportProductConversionData = {
  date: string;
  conversion_percentage: number;
  revenue: number;
  conversion_percentage_previous?: number;
  revenue_previous?: number;
  conversion_percentage_previous_year?: number;
  revenue_previous_year?: number;
};

export type TReportProductConversionRes = TReportProductConversionData[];

export type TReportRefundsByUsedCouponParams = {
  start_date?: string | null;
  end_date?: string | null;
  group?: ReportGroupBy | string;
  coupon_batch?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportRefundsByUsedCouponData = {
  date: string;
  refund_amount: number;
  refund_amount_previous?: number;
  refund_amount_previous_year?: number;
};

export type TReportRefundsByUsedCouponRes = {
  data: TReportRefundsByUsedCouponData[];
};

export type TReportRetentionRateParams = {
  start_date?: string | null;
  end_date?: string | null;
  products?: string[];
};

export type TReportRetentionRateData = {
  date: string;
  products: Record<string, number>;
  products_previous?: Record<string, number>;
  products_previous_year?: Record<string, number>;
};

export type TReportRetentionRateRes = {
  data: TReportRetentionRateData[];
  total_by_product: Record<string, number>;
  available_products: string[];
};

export type TReportRollingConversionParams = {
  start_date: string;
  end_date: string;
  grouping: string;
  rolling_days: number;
  product_ids?: number[];
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportRollingConversionData = {
  date: string;
  conversion_percentage: number;
  conversion_percentage_previous?: number;
  conversion_percentage_previous_year?: number;
};

export type TReportRollingConversionRes = TReportRollingConversionData[];

export type TReportTaxParams = {
  start_date: string | null;
  end_date: string | null;
  group?: ReportGroupBy;
  grouping?: ReportGroupBy;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportTaxData = {
  date: string;
  tax_amount: number;
  tax_amount_previous?: number;
  tax_amount_previous_year?: number;
};

export type TReportTaxRes = {
  data: TReportTaxData[];
  total: number;
};

export type TReportTaxByCustomerCountryParams = {
  start_date: string | null;
  end_date: string | null;
  group?: ReportGroupBy;
  grouping?: ReportGroupBy;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
  countries?: string[];
};

export type TReportTaxByCustomerCountryData = {
  date: string;
  countries: Record<string, number>;
  countries_previous?: Record<string, number>;
  countries_previous_year?: Record<string, number>;
};

export type TReportTaxByCustomerCountryRes = {
  data: TReportTaxByCustomerCountryData[];
  total_by_country: Record<string, number>;
  available_countries: string[];
};

export type TReportTaxByCustomerStateParams = {
  start_date: string | null;
  end_date: string | null;
  group?: ReportGroupBy;
  country: string;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportTaxByCustomerStateData = {
  date: string;
  states: Record<string, number>;
  states_previous?: Record<string, number>;
  states_previous_year?: Record<string, number>;
};

export type TReportTaxByCustomerStateRes = {
  data: TReportTaxByCustomerStateData[];
  total_by_state: Record<string, number>;
  available_states: string[];
};

export type TReportRefundsRateParams = {
  period: string;
  group_by: string;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportRefundsRateData = {
  date: string;
  refunds_rate: number;
  refunds_rate_previous?: number;
  refunds_rate_previous_year?: number;
};

export type TReportRefundsRateRes = {
  data: TReportRefundsRateData[];
};

export type TReportUserDemographicsParams = {
  group_by: 'country' | 'state';
  page?: number;
  size?: number;
};

export type TReportUserDemographicsData = {
  location: string;
  location_code: string;
  user_count: number;
};

export type TReportUserDemographicsRes = {
  data: TReportUserDemographicsData[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type TReportUserDistributionByCustomFieldParams = {
  field: string;
};

export type TReportUserDistributionByCustomFieldData = {
  field_value: string;
  user_count: number;
};

export type TReportUserDistributionByCustomFieldRes = {
  data: TReportUserDistributionByCustomFieldData[];
  total: number;
  field: string;
};

export type TReportUserDistributionByCustomFieldByDateParams = {
  start_date: string | null;
  end_date: string | null;
  grouping?: ReportGroupBy;
  group?: ReportGroupBy;
  compare_to_previous_period?: boolean;
  compare_to_same_period_previous_year?: boolean;
};

export type TReportUserDistributionByCustomFieldByDateData = {
  date: string;
  field_values: Record<string, number>;
  field_values_previous?: Record<string, number>;
  field_values_previous_year?: Record<string, number>;
};

export type TReportUserDistributionByCustomFieldByDateRes =
  TReportUserDistributionByCustomFieldByDateData[];

export type TReportUserLifetimeParams = {
  group_period_by_days: number;
};

export type TReportUserLifetimeData = {
  lifetime_range: string;
  number_of_users: number;
};

export type TReportUserLifetimeRes = {
  data: TReportUserLifetimeData[];
  total: number;
};

export type TReportUsersByUserGroupsData = {
  user_group: string;
  user_count: number;
};

export type TReportUsersByUserGroupsRes = {
  data: TReportUsersByUserGroupsData[];
  total: number;
};

export type TPADailyPLParams = {
  start_date?: string;
  live_accounts?: boolean;
  profit?: number;
};

export type TPADailyPLData = {
  package_id: number;
  package_name: string;
  days_3: number;
  days_5: number;
  days_7: number;
  days_10: number;
  days_15: number;
  days_20: number;
  days_25: number;
  days_30: number;
};

export type TPADailyPLRes = {
  data: TPADailyPLData[];
  total_packages: number;
  generated_at: string;
  title: string;
};

export type TReportEvalAccountAnalysisParams = {
  start_date?: string; // Format: YYYY-MM-DD, default to today
  end_date?: string; // Format: YYYY-MM-DD, default to today+1
};

export type TReportEvalAccountAnalysisDetailRow = {
  package: string;
  total_accounts: number;
  avg_days: number;
  resets: number;
  reset_percent: string;
  accounts_5_days: number;
  percent_5_days: string;
  accounts_10_days: number;
  percent_10_days: string;
  accounts_14_days: number;
  percent_14_days: string;
  accounts_30_days: number;
  percent_30_days: string;
  accounts_60_days: number;
  percent_60_days: string;
  accounts_90_days: number;
  percent_90_days: string;
  accounts_120_days: number;
  percent_120_days: string;
  accounts_150_days: number;
  percent_150_days: string;
  accounts_180_days: number;
  percent_180_days: string;
  accounts_210_days: number;
  percent_210_days: string;
  accounts_240_days: number;
  percent_240_days: string;
  accounts_270_days: number;
  percent_270_days: string;
  accounts_300_days: number;
  percent_300_days: string;
  accounts_330_days: number;
  percent_330_days: string;
  accounts_360_days: number;
  percent_360_days: string;
};

export type TReportEvalAccountAnalysisSummaryRow = {
  description: string;
  amount: number;
  percent: string;
  avg_days: number | null;
  max_days: number | null;
};

export type TReportEvalAccountAnalysisRes = {
  title: string;
  title_summary: string;
  generated_at: string;
  detail_rows: TReportEvalAccountAnalysisDetailRow[];
  summary_rows: TReportEvalAccountAnalysisSummaryRow[];
};

export type TEvalAnalysisDayColumn = {
  count: number;
  percentage: number;
};

export type TEvalAnalysisData = {
  package: string;
  total_accounts: number;
  avg_days: number;
  resets: number;
  resets_percentage: number;
  days_5: TEvalAnalysisDayColumn;
  days_10: TEvalAnalysisDayColumn;
  days_14: TEvalAnalysisDayColumn;
  days_30: TEvalAnalysisDayColumn;
  days_60: TEvalAnalysisDayColumn;
  days_90: TEvalAnalysisDayColumn;
  days_120: TEvalAnalysisDayColumn;
  days_150: TEvalAnalysisDayColumn;
  days_180: TEvalAnalysisDayColumn;
  days_210: TEvalAnalysisDayColumn;
  days_240: TEvalAnalysisDayColumn;
  days_270: TEvalAnalysisDayColumn;
  days_300: TEvalAnalysisDayColumn;
  days_330: TEvalAnalysisDayColumn;
  days_360: TEvalAnalysisDayColumn;
};

export type TEvalAnalysisSummaryData = {
  status: string;
  amount: number;
  percentage: number;
  avg_days: number | null;
  max_days: number | null;
};

export type TEvalAnalysisByCreationDateParams = {
  start_date: string | null;
  end_date: string | null;
};

export type TEvalAnalysisByCreationDateRes = {
  analysis: TEvalAnalysisData[];
  summary: TEvalAnalysisSummaryData[];
  total_accounts: number;
  date_range: string;
};

export type TBundlesPAAccountsMonthData = {
  evals: number;
  fill: number;
  payouts: number;
  paid: number;
};

export type TBundlesPAAccountsData = {
  package: string;
  jan: TBundlesPAAccountsMonthData;
  feb: TBundlesPAAccountsMonthData;
  mar: TBundlesPAAccountsMonthData;
  apr: TBundlesPAAccountsMonthData;
  may: TBundlesPAAccountsMonthData;
  jun: TBundlesPAAccountsMonthData;
  jul: TBundlesPAAccountsMonthData;
  aug: TBundlesPAAccountsMonthData;
  sep: TBundlesPAAccountsMonthData;
  oct: TBundlesPAAccountsMonthData;
  nov: TBundlesPAAccountsMonthData;
  dec: TBundlesPAAccountsMonthData;
};

export type TBundlesPAAccountsParams = {
  year: number;
};

// New API response types
export type TBundlePAAccountMonthData = {
  month: string;
  eval_accounts: number;
  pa_accounts: number;
  payout_accounts: number;
  payout_total_amount: string;
  pa_percent: string;
  payout_percent: string;
};

export type TBundlePAAccountPackageData = {
  package_name: string;
  months: TBundlePAAccountMonthData[];
};

export type TBundlePAAccountRes = {
  data: TBundlePAAccountPackageData[];
  totals: TBundlePAAccountMonthData[];
  year: number;
  generated_at: string;
};

export type TPAPlnAnalysisParams = {
  from_date?: string;
  to_date?: string;
  fetch_all?: boolean;
};

export type TPAPlnAnalysisData = {
  package: string;
  row_type: string;
  total_pa_accounts: string;
  total_payouts: string;
  pnl_5_days: string;
  pnl_10_days: string;
  pnl_15_days: string;
  pnl_20_days: string;
  pnl_25_days: string;
  pnl_30_days: string;
  pnl_60_days: string;
  pnl_90_days: string;
  pnl_120_days: string;
  pnl_150_days: string;
  accounts_5_days: string;
  users_5_days: number;
  accounts_10_days: string;
  users_10_days: number;
  accounts_15_days: string;
  users_15_days: number;
  accounts_20_days: string;
  users_20_days: number;
  accounts_25_days: string;
  users_25_days: number;
  accounts_30_days: string;
  users_30_days: number;
  accounts_60_days: string;
  users_60_days: number;
  accounts_90_days: string;
  users_90_days: number;
  accounts_120_days: string;
  users_120_days: number;
  accounts_150_days: string;
  users_150_days: number;
};

export type TPAPlnAnalysisRes = {
  data: TPAPlnAnalysisData[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  generated_at: string;
};

export type TPAActivePayoutsAddonsData = {
  id?: number;
  name: string;
  user_id?: number;
  email?: string;
  account_id: string;
  account_package: string;
  eval_purchase: string;
  valid: string;
  status?: string;
  auto_liquidated_on?: string | null;
  notes?: string | null;
  trading_days: number;
  balance: string;
  net_pl: string;
  acc_payouts: string;
  total_paid_by_customer: string;
  acc_payout_amount: string;
  addon_reduced_days_to_qualify: boolean;
  addon_extra_days_200: boolean;
  addon_30_day_payouts: boolean;
  addon_15_day_payouts: boolean;
  addon_lifetime_days_179: boolean;
  addon_lifetime_days_365: boolean;
  addon_performance_fee_monthly?: boolean;
  addon_performance_fee_lifetime_one_time?: boolean;
  addon_performance_fee_lifetime_pay_upfront?: boolean;
  addon_performance_fee_lifetime_pay_partial?: boolean;
};

export type TPAActivePayoutsAddonsRequest = {
  product_id?: number;
  account_status?: 'show_all' | 'active_only';
  payout_status?: 'show_all' | 'with_payout_only';
  page?: number;
  page_size?: number;
};

export type TPAActivePayoutsAddonsApiRow = {
  id: number;
  user_id: number;
  name: string;
  email: string;
  account_id: string | null;
  package: string | null;
  eval_purchase_date: string | null;
  valid_from: string | null;
  valid_to: string | null;
  status: string | null;
  auto_liquidated_on: string | null;
  notes: string | null;
  trading_days: number;
  balance: number;
  net_pnl: number;
  payouts_count: number;
  payouts_amount: number;
  total_paid: number;
  eval_quality_x_days: boolean;
  eval_reduced_pa_fee: boolean;
  pa_extra_amount: boolean;
  pa_payout_x_days_10: boolean;
  pa_payout_x_days_15: boolean;
  performance_fee_monthly: boolean;
  performance_fee_lifetime_one_time: boolean;
  performance_fee_lifetime_pay_upfront: boolean;
  performance_fee_lifetime_pay_partial: boolean;
};

export type TPAActivePayoutsAddonsApiRes = {
  data: TPAActivePayoutsAddonsApiRow[];
  total_count: number;
  current_page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  generated_at: string;
};

// E-commerce Dashboard Drill-down Types

export type TRevenueBreakdownItem = {
  package_id: number;
  package_name: string;
  revenue: number;
  order_count: number;
  percentage: number;
};

export type TReportRevenueBreakdownRes = {
  total_revenue: number;
  breakdown: TRevenueBreakdownItem[];
};

export type TTopSellingProductItem = {
  package_id: number;
  package_name: string;
  quantity_sold: number;
  revenue: number;
  average_price: number;
};

export type TReportTopSellingProductsRes = {
  total_products_sold: number;
  top_products: TTopSellingProductItem[];
};

export type TLTVDistributionItem = {
  range_label: string;
  range_min: number;
  range_max: number | null;
  customer_count: number;
  percentage: number;
  total_revenue: number;
};

export type TReportLTVDistributionRes = {
  total_customers: number;
  average_ltv: number;
  distribution: TLTVDistributionItem[];
};

// E-commerce Dashboard Summary API Types
export type TEcommerceDashboardSummaryPayload = {
  start_date: string;
  end_date: string;
};

export type TEcommerceDashboardSummaryRes = {
  total_revenue_mdt: number;
  total_products_sold: number;
  average_customer_ltv: number;
  top_affiliate: number;
};

// E-commerce Dashboard Chart API Types
export type TEcommerceDashboardChartPayload = {
  start_date: string;
  end_date: string;
};

export type TEcommerceDashboardChartSeries = {
  type: string;
  data: number[];
};

export type TEcommerceDashboardChartRes = {
  months: string[];
  series: TEcommerceDashboardChartSeries[];
};

// Payout Dashboard Summary API Types
export type TPayoutDashboardSummaryRes = {
  active_users: number;
  active_accounts: number;
  payout_total: number;
  pa_accounts: number;
};

// Payout Dashboard Chart API Types
export type TPayoutDashboardChartPayload = {
  start_date: string;
  end_date: string;
};

export type TPayoutDashboardChartSeries = {
  platform: string;
  data: number[];
};

export type TPayoutDashboardChartRes = {
  months: string[];
  series: TPayoutDashboardChartSeries[];
};

// Affiliate Payout Reports
export type TReportAffiliatePayoutData = {
  userName: string;
  payout: number;
  isTotal?: boolean;
};

export type TReportAffiliatePayoutParams = {
  start_date?: string | null;
  end_date?: string | null;
  minimum_payout?: number;
};

export type TReportAffiliatePayoutRes = TReportAffiliatePayoutData[];

// Leaderboard Report Types
export type TLeaderBoardPeriod = 'daily' | 'weekly' | 'monthly';

export type TReportLeaderBoardParams = {
  product_id?: number | null;
  show_top: number;
  period: TLeaderBoardPeriod;
  date: string;
};

export type TReportLeaderBoardRow = {
  user_id: number;
  rank: number;
  user_display_name: string;
  user_name: string;
  account: string;
  profit: string;
  balance: string;
  trades: number;
  win_rate: number;
};

export type TReportLeaderBoardRes = {
  data: TReportLeaderBoardRow[];
};

export type TReportEvalsToPaParams = {
  package_id?: number | null;
  from_date?: string | null;
  to_date?: string | null;
};

export type TReportEvalsToPaItem = {
  package: string;
  evals_total: number;
  eval_users_total: number;
  pa_total: number;
  pa_users_total: number;
  pa_active_total: number;
  pa_active_users_total: number;
  payouts_total: number;
  payouts_amount: string;
  percent_pa_to_payout: string;
};

export type TReportEvalsToPaRes = {
  data: TReportEvalsToPaItem[];
  total_packages: number;
  generated_at: string;
};

// ── Payouts Totals Report ──────────────────────────────────────────

export type TPayoutsByPackageAnalysisRes = {
  package: string;
  total_pa_accounts: number;
  total_payouts: number;
  unique_accounts: number;
  unique_users: number;
  total_payout_amount: string;
  accounts_with_1_payout: number;
  accounts_with_2_payouts: number;
  accounts_with_3_payouts: number;
  accounts_with_4_payouts: number;
  accounts_with_5_payouts: number;
  accounts_with_6_payouts: number;
  accounts_with_7_payouts: number;
};

export type TPayoutsByPackageTotalsRes = {
  package: string;
  total_pa_accounts: number;
  total_payouts: number;
  unique_accounts: number;
  unique_users: number;
  total_payout_amount: string;
  accounts_with_1_payout: number;
  accounts_with_2_payouts: number;
  accounts_with_3_payouts: number;
  accounts_with_4_payouts: number;
  accounts_with_5_payouts: number;
  accounts_with_6_payouts: number;
  accounts_with_7_payouts: number;
};

export type TPayoutsByMonthRes = {
  month: string;
  amount: string;
};

export type TPayoutsByMonthTotalsRes = {
  month: string;
  amount: string;
};

export type TPayoutsTotalsReportResponse = {
  payouts_by_package: TPayoutsByPackageAnalysisRes[];
  payouts_by_package_totals: TPayoutsByPackageTotalsRes;
  payouts_by_month: TPayoutsByMonthRes[];
  payouts_by_month_totals: TPayoutsByMonthTotalsRes;
  generated_at: string;
};
